import { ButtonGroup, TextLink } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Modal, ModalBody } from "@vds/modals";
import { RadioButtonGroup } from "@vds/radio-buttons";
import { Body, Title } from "@vds/typography";
import { useEffect, useState } from "react";
import { CVAPP_CAMERAADJUSTMENT, CVAPP_OBJECTBLUR } from "../../constants/cvappConstants";
import {
  BASE_LICENSE_SKUS,
  isV2Sku,
  LICENSE_LABELS,
  LICENSE_MODAL_TEXTS,
  LICENSE_TYPES,
  LICENSE_VALUES,
  licensesMessage
} from "../../constants/licensesConstants";
import { getLicenseTypeDisplayed } from "../../utility/Utils";
import { Box } from "@mui/material";

const ChangeLicenseModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentSku,
  licenseList,
  roiList,
  deviceId,
  title = LICENSE_MODAL_TEXTS.CHANGE_LICENSE_TYPE,
  primaryButtonLabel = LICENSE_MODAL_TEXTS.YES_CHANGE,
  secondaryButtonLabel = LICENSE_MODAL_TEXTS.NO_CANCEL,
  upgradeMode = false,
  infoMessage = "",
  onUnsubscribeClick = () => {}
}) => {
  const [selectedSku, setSelectedSku] = useState("");
  const [radioData, setRadioData] = useState([]);
  const [originalList, setOriginalList] = useState([]);
  const [localList, setLocalList] = useState([]);
  const [switchMessage, setSwitchMessage] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);

  const generateRadioData = (currentLicenseType, availableList, activeSku = "", activeMessage = "") => {
    const filtered = availableList?.filter(
      (lic) => getLicenseTypeDisplayed(lic.sku, licenseList) !== getLicenseTypeDisplayed(currentLicenseType, licenseList)
    );
    const otherOptions = (filtered || []).map((license) => {
      const isSelected = license.sku === activeSku && !!activeMessage;
      return {
        label:
          license.displayName === LICENSE_TYPES.STARTER
            ? LICENSE_LABELS.STARTER_LIMITED(license.displayName)
            : LICENSE_LABELS.LICENSE_UNLIMITED(license.displayName),
        children: isSelected
          ? <span>
              {`${license.availablelicenses} ${LICENSE_LABELS.AVAILABLE_SUFFIX}`}
              <div className="licensemodal-text">
                <Icon name="error" size="small" color="#C44904" />
                <span className="icon-color">{activeMessage}</span>
              </div>
            </span>
          : `${license.availablelicenses} ${LICENSE_LABELS.AVAILABLE_SUFFIX}`,
        value: license.sku,
        ariaLabel: license.sku
      };
    });
    if (upgradeMode) return otherOptions;
    const isNoLicenseSelected = activeSku === LICENSE_VALUES.NO_LICENSE && !!activeMessage;
    const noLicenseOption = {
      label: LICENSE_LABELS.NO_LICENSE,
      children: isNoLicenseSelected
        ? <span>
            {LICENSE_LABELS.REMOVE_LICENSE_DESC}
          <div className="licensemodal-text">
              <Icon name="error" size="small" color="#C44904" />
              <span className="icon-color">{activeMessage}</span>
            </div>
          </span>
        : LICENSE_LABELS.REMOVE_LICENSE_DESC,
      value: LICENSE_VALUES.NO_LICENSE,
      ariaLabel: LICENSE_VALUES.NO_LICENSE
    };
    return [noLicenseOption, ...otherOptions];
  };

  useEffect(() => {
    if (!isOpen) return;
    // const original = licenseList?.filter(
    //   (lic) => getLicenseTypeDisplayed(lic.sku, licenseList) !== getLicenseTypeDisplayed(currentSku, licenseList)
    // );
    // Only show primary (non-addon) licenses matching the stream's version (v1 or v2).
    // Prevents add-ons like Enrolled Appearance from appearing as switchable options
    // and prevents cross-version options (e.g. v1 licenses for a v2 stream).
    const isV2Stream = isV2Sku(currentSku);
    const original = licenseList?.filter((lic) =>
      BASE_LICENSE_SKUS.includes(lic.sku) &&
      getLicenseTypeDisplayed(lic.sku, licenseList) !== getLicenseTypeDisplayed(currentSku, licenseList) &&
      isV2Sku(lic.sku) === isV2Stream
    );
    setOriginalList(original);
    setLocalList(JSON.parse(JSON.stringify(original)));
    const options = generateRadioData(currentSku, original);
    setRadioData(options);
    const autoSelected = upgradeMode && options.length === 1 ? options[0].value : "";
    setSelectedSku(autoSelected);
    if (autoSelected) {
      const autoSelectedLicense = original?.find((lic) => lic.sku === autoSelected);
      if (autoSelectedLicense?.availablelicenses === 0) {
        const message = licensesMessage.ZERO_LICENSE_AVAILABLE_MESSAGE;
        setSwitchMessage(message);
        setIsDisabled(true);
        setRadioData(generateRadioData(currentSku, original, autoSelected, message));
        return;
      }
    }
    setSwitchMessage("");
    setIsDisabled(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setRadioData(generateRadioData(currentSku, localList, selectedSku, switchMessage));
  }, [switchMessage, selectedSku]); 

  const handleClose = () => {
    setLocalList(originalList);
    setRadioData(generateRadioData(currentSku, originalList, "", ""));
    setSelectedSku("");
    setSwitchMessage("");
    onClose();
  };

  const handleRadioChange = (e) => {
    const value = e.target.value;
    setSelectedSku(value);

    const updatedLocal =
      value !== LICENSE_VALUES.NO_LICENSE
        ? originalList.map((lic) =>
            lic.sku === value && lic.availablelicenses > 0
              ? { ...lic, availablelicenses: lic.availablelicenses - 1 }
              : lic
          )
        : originalList;
    setLocalList(updatedLocal);
    setRadioData(generateRadioData(currentSku, updatedLocal));

    const availCount = updatedLocal.find((lic) => lic.sku === value)?.availablelicenses;
    if (availCount === 0 && value !== LICENSE_VALUES.NO_LICENSE) {
      setSwitchMessage(licensesMessage.ZERO_LICENSE_AVAILABLE_MESSAGE);
      setIsDisabled(true);
      return;
    }

    const matchingROIwithCVApps = roiList?.filter((roi) => roi.parentdeviceid === deviceId && roi.cvapps != undefined);
    const matchingROI = [...new Set(matchingROIwithCVApps?.map((roi) => roi?.cvapps[0].image))];
    const matchingIds = matchingROIwithCVApps?.filter((roi) =>
      roi.cvapps?.some((h) => h.name === CVAPP_OBJECTBLUR || h.name === CVAPP_CAMERAADJUSTMENT)
    );
    if (value === LICENSE_VALUES.NO_LICENSE) {
      if (matchingROI.length > 0) {
        setSwitchMessage(
          <>
            {licensesMessage.NO_LICENSE_SWITCH_MESSAGE_PREFIX}
            <TextLink kind="standalone" size="small" onClick={onUnsubscribeClick}>
              {licensesMessage.NO_LICENSE_SWITCH_MESSAGE_LINK}
            </TextLink>
            {licensesMessage.NO_LICENSE_SWITCH_MESSAGE_SUFFIX}
          </>
        );
        setIsDisabled(true);
      } else {
        setSwitchMessage("");
        setIsDisabled(false);
      }
      return;
    }

    const selectedType = getLicenseTypeDisplayed(value, licenseList);

    if (selectedType === LICENSE_TYPES.STARTER) {
      const isCountExceeded = matchingROI.length > 3;
      const allCvApps = matchingIds.flatMap((roi) => roi.cvapps || []);
      const hasObjectBlur = allCvApps.some((app) => app.name === CVAPP_OBJECTBLUR);
      const hasCameraAdj = allCvApps.some((app) => app.name === CVAPP_CAMERAADJUSTMENT);
      const hasCvApp = hasObjectBlur || hasCameraAdj;

      if (isCountExceeded || hasCvApp) {
        let prefix;
        let suffix = licensesMessage.STARTER_SWITCH_MESSAGE_SUFFIX;
        if (isCountExceeded && hasCvApp) {
          prefix = licensesMessage.STARTER_SWITCH_COMBINED_CVAPP_PREFIX;
          suffix = licensesMessage.STARTER_SWITCH_MESSAGE_APPS_SUFFIX;
        } else if (isCountExceeded) {
          prefix = licensesMessage.STARTER_SWITCH_MESSAGE_PREFIX;
          suffix = licensesMessage.STARTER_SWITCH_MESSAGE_APPS_SUFFIX;
        } else {
          prefix = licensesMessage.STARTER_SWITCH_CVAPP_PREFIX;
          suffix = licensesMessage.STARTER_SWITCH_EXTRA_FEATURES_SUFFIX;
        }
        setIsDisabled(true);
        setSwitchMessage(
          <>
            {prefix}
            <TextLink kind="standalone" size="small" onClick={onUnsubscribeClick}>
              {licensesMessage.STARTER_SWITCH_MESSAGE_LINK}
            </TextLink>
            {suffix}
          </>
        );
      } else {
        setSwitchMessage("");
        setIsDisabled(false);
      }
    } else {
      setSwitchMessage("");
      setIsDisabled(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      hideCloseButton={true}
      disableOutsideClick={true}
      closeButton={
        <div className="modal-close-button" onClick={handleClose}>
          <Icon name="close" />
        </div>
      }
    >
      <div className="change-license-modal-title">
        <Title size="large" bold primitive="h2" viewport="desktop">
          {title}
        </Title>
      </div>
      <ModalBody>
        <div style={{ overflow: "hidden" }}>
          <div className="modal-body-text change-license-modal-body-text">
            <Body size="large" viewport="desktop" primitive="div">
              {LICENSE_MODAL_TEXTS.CURRENTLY_SELECTED} : <strong>{getLicenseTypeDisplayed(currentSku, licenseList)}</strong>
            </Body>
          </div>
          {infoMessage !== "" && (
            <div className="licensemodal-info-text">
              <Icon name="error" size="medium" color="#C44904" />
              <Body size="medium" viewport="desktop" primitive="div" color="#C44904">
                {infoMessage}
              </Body>
            </div>
          )}
          <Body size="medium" viewport="desktop" primitive="span" color="#888888">
            <RadioButtonGroup
              onChange={handleRadioChange}
              error={false}
              value={selectedSku || currentSku}
              data={radioData}
            />
          </Body>
          <div className="license-details-button-group-container">
            <ButtonGroup
              viewport="desktop"
              rowQuantity={{ desktop: 2 }}
              gap="16px"
              alignment="center"
              data={[
                {
                  children: primaryButtonLabel,
                  size: "medium",
                  use: "primary",
                  width: "226px",
                  disabled: !selectedSku || isDisabled,
                  "data-testid": "licenses_yesChangeButton",
                  onClick: () => onConfirm(selectedSku)
                },
                {
                  children: secondaryButtonLabel,
                  size: "medium",
                  use: "secondary",
                  width: "226px",
                  "data-testid": "licenses_noCancelButton",
                  onClick: handleClose
                }
              ]}
            />
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ChangeLicenseModal;
